import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * PDP metafield accordions (ingredients, nutrition, allergen) that update per variant.
 * Shipping stays product-level and does not change on variant select.
 *
 * @extends {Component<Refs>}
 */
class KilwinsProductAccordions extends Component {
  /** @typedef {{ ingredients?: HTMLElement, nutrition?: HTMLElement, allergen?: HTMLElement, shipping?: HTMLElement }} Refs */

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#renderFromDataset();
    this.#decorateAllAccordionImages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { html, newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const updatedAccordions = Array.from(html?.querySelectorAll('kilwins-product-accordions') ?? []).find(
          (element) => element.dataset.blockId === this.dataset.blockId
        );

        if (updatedAccordions) {
          this.#syncDatasets(updatedAccordions);
        }

        const variantId = resource?.id ?? updatedAccordions?.dataset.variantId ?? this.dataset.variantId ?? '';
        this.dataset.variantId = String(variantId);
        this.#updateVariantAccordions(variantId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kilwins-product-accordions]', error);
      });
  };

  /** @param {HTMLElement} source */
  #syncDatasets(source) {
    const keys = ['variantIngredients', 'variantNutrition', 'variantAllergens', 'variantId'];

    for (const key of keys) {
      if (source.dataset[key] !== undefined) {
        this.dataset[key] = source.dataset[key];
      }
    }
  }

  #renderFromDataset() {
    this.#updateVariantAccordions(this.dataset.variantId);
  }

  /**
   * @param {string | number | undefined} variantId
   */
  #updateVariantAccordions(variantId) {
    this.#updateAccordionRow('ingredients', this.#getHtmlForVariant('variantIngredients', variantId));
    this.#updateAccordionRow('nutrition', this.#getHtmlForVariant('variantNutrition', variantId));
    this.#updateAccordionRow('allergen', this.#getHtmlForVariant('variantAllergens', variantId));
    this.#updateContainerVisibility();
  }

  /**
   * @param {string} accordionKey
   * @param {string} html
   */
  #updateAccordionRow(accordionKey, html) {
    const row = this.querySelector(`[data-accordion="${accordionKey}"]`);
    if (!(row instanceof HTMLElement)) return;

    const content = row.querySelector('[data-accordion-content]');
    if (!(content instanceof HTMLElement)) return;

    if (!html) {
      row.hidden = true;
      content.innerHTML = '';
      const details = row.querySelector('details');
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
      return;
    }

    row.hidden = false;
    content.innerHTML = html;
    this.#decorateAccordionImages(content, accordionKey);
  }

  /**
   * @param {HTMLElement} content
   * @param {string} accordionKey
   */
  #decorateAllAccordionImages() {
    for (const row of this.querySelectorAll('[data-accordion]')) {
      if (!(row instanceof HTMLElement)) continue;
      const content = row.querySelector('[data-accordion-content]');
      if (!(content instanceof HTMLElement)) continue;
      this.#decorateAccordionImages(content, row.dataset.accordion ?? '');
    }
  }

  /**
   * @param {HTMLElement} content
   * @param {string} accordionKey
   */
  #decorateAccordionImages(content, accordionKey) {
    const altText = this.#getAccordionImageAlt(accordionKey);
    if (!altText) return;

    for (const image of content.querySelectorAll('img')) {
      if (!(image instanceof HTMLImageElement)) continue;
      if (!image.getAttribute('alt')?.trim()) {
        image.setAttribute('alt', altText);
      }
    }
  }

  /**
   * @param {string} accordionKey
   * @returns {string}
   */
  #getAccordionImageAlt(accordionKey) {
    const datasetKeyMap = {
      nutrition: 'nutritionImageAlt',
      ingredients: 'ingredientsImageAlt',
      allergen: 'allergenImageAlt',
    };
    const datasetKey = datasetKeyMap[accordionKey];
    return datasetKey ? this.dataset[datasetKey] ?? '' : '';
  }

  /**
   * @param {'variantIngredients' | 'variantNutrition' | 'variantAllergens'} datasetKey
   * @param {string | number | undefined} variantId
   * @returns {string}
   */
  #getHtmlForVariant(datasetKey, variantId) {
    if (!variantId || !this.dataset[datasetKey]) return '';

    try {
      const variantMap = JSON.parse(this.dataset[datasetKey]);
      const html = variantMap[variantId] ?? variantMap[String(variantId)] ?? '';
      return typeof html === 'string' ? html : '';
    } catch {
      return '';
    }
  }

  #updateContainerVisibility() {
    const rows = this.querySelectorAll('[data-accordion]');
    const anyVisible = Array.from(rows).some((row) => row instanceof HTMLElement && !row.hidden);
    this.hidden = !anyVisible;
  }
}

if (!customElements.get('kilwins-product-accordions')) {
  customElements.define('kilwins-product-accordions', KilwinsProductAccordions);
}
